import { observer } from 'mobx-react';
import React from 'react';
import { makeAutoObservable } from 'mobx';
import {
	DefaultButton,
	Dialog,
	DialogFooter,
	DialogType,
	Dropdown,
	MessageBar,
	MessageBarType,
	PrimaryButton,
	Spinner,
	SpinnerSize
} from '@fluentui/react';
import { EntityAPI, IEntityContentItem, IEntityDocumentResult, IEntityTypeUserInfo, IUserConfigPackage } from '../../API';

export interface ITemplateDocumentsListProps {
	state: TemplateDocumentsListState;
}

class TemplateDocumentsList extends React.Component<ITemplateDocumentsListProps,{}> {
	render() {

		const modelProps = {
			isBlocking: true,
			styles: {},
		};

		const dialogContentProps = {
			type: DialogType.normal,
			title: 'Create document',
		};


		return <Dialog
			dialogContentProps={dialogContentProps}
			modalProps={modelProps}
			hidden={!this.props.state.visible}
			onDismiss={() => {
				this.props.state.hide();
			}}>
				
			{ this.getMessage() }

			{ this.props.state.loading ? <Spinner size={SpinnerSize.large} /> : <></> }

			{this.props.state.contents && <>

				<Dropdown
					placeholder="Select a document"
					options={this.props.state.contents.documents.map(item => ({
						key: item.id,
						text: item.name
					}))}
					defaultSelectedKey={this.props.state.selected?.id}
					onChange={(_, option) => {
						if(this.props.state.contents){
							let selected = option ? this.props.state.contents.documents.find(item => item.id === option.key) : undefined;
							this.props.state.setSelected(selected);	
						}
					}}
				/>

			</>}

			<DialogFooter>{this.getCancelSaveButtons()}</DialogFooter>
		</Dialog>;
	}

	getMessage(): React.ReactNode {
		if(this.props.state.generating) {
			return <MessageBar messageBarType={MessageBarType.success}>Generating document, please wait...</MessageBar>;
		}

		if(this.props.state.loading) {
			return <MessageBar messageBarType={MessageBarType.info}>Loading available templates...</MessageBar>;
		}

		if((this.props.state.contents?.documents || []).length === 0) {
			return <MessageBar messageBarType={MessageBarType.warning}>No template documents are available.</MessageBar>;
		}
		else {
			return <MessageBar>Select the document to generate and upload to this {this.props.state.entityType?.singularName}.</MessageBar>;
		}
	}

	
	private getCancelSaveButtons(): React.ReactNode {
		return (<>
			<DefaultButton
				style={{ width: 100 }}
				onClick={() => {
					this.props.state.hide();
				}}
			>
				Close
			</DefaultButton>

			<PrimaryButton
				disabled={this.props.state.generating}
				hidden={!this.props.state.selected}
				style={{ marginLeft: 10 }}
				onClick={() => {
					this.props.state.addDocument();
				}}>
				Add document
			</PrimaryButton>
		</>);				
				
	}
}

export class TemplateDocumentsListState {

	public userConfig: IUserConfigPackage;
	
	public entityType?: IEntityTypeUserInfo;
	public id?: string;

	public loading:boolean = false;
	public visible:boolean = false;
	public generating:boolean = false;

	public contents?: IEntityDocumentResult;

	public selected?:IEntityContentItem;
	public targetUrl?: string;
	private onCompleteCallback?: () => void;

	constructor(userConfig: IUserConfigPackage) {
		makeAutoObservable(this);
		this.userConfig = userConfig;
	}

	hide() {
		this.visible = false;
	}

	public setGenerating(value:boolean) {
		this.generating = value;
	}
	
	setSelected(selected: IEntityContentItem | undefined) {
		this.selected = selected;
	}
	
	async addDocument() {
		this.setGenerating(true);

		let api = EntityAPI.fromEntityTypeId(this.entityType?.id || '', await this.userConfig.getToken());
		await api.addDocument(this.id || '', this.selected?.webUrl || '', this.targetUrl || '');

		if(this.onCompleteCallback) {
			this.onCompleteCallback();
		}

		this.hide();
		this.setGenerating(false);
	}
	

	public show(entityType:string, id:string, targetUrl:string) {
		this.targetUrl = targetUrl;
		this.entityType = this.userConfig.entityTypes.getConfig(entityType);
		this.id = id;
		this.visible = true;

		//Load the documents
		this.loadDocuments();
	}

	public onComplete(callback:() => void) {
		this.onCompleteCallback = callback;
	}
	
	private async loadDocuments() {
		
		this.setLoading(true);

		let availableDocuments = await this.getCachedDocumentsAndRefresh();

		this.setDocuments(availableDocuments);
		this.setLoading(false);
	}

	public async getCachedDocumentsAndRefresh() : Promise<IEntityDocumentResult|undefined> {
		let key = `templateDocuments_${this.entityType?.id}`;
		let storedDocsStr = window.localStorage.getItem(key);
		if(!storedDocsStr) {
			//Get them from API
			let docs = await this.loadDocsFromAPI();
			window.localStorage.setItem(key, JSON.stringify(docs));

			return docs;
		} else  {
			//In the background, refresh the documents and put them in local storage
			this.loadDocsFromAPI().then((docs) => {
				if(docs) {
					window.localStorage.setItem(key, JSON.stringify(docs));
				}
			});
		}

		return JSON.parse(storedDocsStr);

	}

	private async loadDocsFromAPI() : Promise<IEntityDocumentResult|undefined> {
		if(this.entityType?.id) {
			let api = EntityAPI.fromEntityTypeId(this.entityType.id, await this.userConfig.getToken());
			return await api.documentTemplates();
		}
	}

	public setLoading(arg0: boolean) {
		this.loading = arg0;
	}

	private setDocuments(arg0?: IEntityDocumentResult) {
		this.contents = arg0;
	}

}

export default observer(TemplateDocumentsList);
